import { foldText } from '../../utils/fuzzySearch';
import type { DefunctBodyView, GovActView, SeatView, TenureView, TimelineView } from './types';

/**
 * The timeline as it is drawn and read: time scales, rows, colours and what
 * each act did — all pure, so the chart, the chronicle and the seats agree.
 */

export type TimelineMode = 'chart' | 'acts' | 'seats';
export type TimelineLevel = '1' | '2' | '3' | 'all';
export type TimelineColour = 'person' | 'post' | 'ending';

export const LEVELS: Array<{ value: TimelineLevel; label: string }> = [
  { value: '1', label: 'Top' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: 'all', label: 'All' },
];

/** What each depth shows, said plainly for a tooltip */
export const LEVEL_HINT: Record<TimelineLevel, string> = {
  '1': 'Heads of state and government',
  '2': '…and the members of the government',
  '3': '…and the secretaries general and cabinets',
  all: 'Every post the acts name',
};

export const TIER_LABEL: Record<SeatView['tier'], string> = {
  1: 'Heads of state and government',
  2: 'Members of the government',
  3: 'Secretaries general and cabinets',
  4: 'Other posts',
};

export const tierShown = (tier: SeatView['tier'], level: TimelineLevel): boolean =>
  level === 'all' ? true : tier <= Number(level);

/* ── Time ─────────────────────────────────────────────────────────── */

const DAY = 86_400_000;

/** Days since 1970 for "2026-07-27", or the first of the month for "2026-07" */
export const dayOf = (date: string): number => {
  const [year, month = '01', day = '01'] = date.slice(0, 10).split('-');
  return Math.floor(Date.UTC(Number(year), Number(month) - 1, Number(day)) / DAY);
};

export const isoOf = (day: number): string => new Date(day * DAY).toISOString().slice(0, 10);

export const todayDay = (): number => Math.floor(Date.now() / DAY);

/** The stretch of time the timeline covers, with a little room either side */
export function spanOf(view: TimelineView): { from: number; to: number } {
  const days = [
    ...view.tenures.map((tenure) => dayOf(tenure.from)),
    ...view.acts.map((act) => dayOf(act.date)),
  ];
  const today = todayDay();
  if (!days.length) return { from: today - 365, to: today + 60 };
  return { from: Math.min(...days) - 45, to: today + 75 };
}

export interface Tick {
  day: number;
  label: string;
  major: boolean;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Years always; months (or quarters) once there is room to read them */
export function ticksOf(from: number, to: number, pxPerDay: number): Tick[] {
  const ticks: Tick[] = [];
  const start = new Date(from * DAY);
  const end = new Date(to * DAY);
  const monthWidth = pxPerDay * 30.4;
  const step = monthWidth >= 34 ? 1 : monthWidth >= 12 ? 3 : 12;
  for (let year = start.getUTCFullYear(); year <= end.getUTCFullYear(); year++) {
    for (let month = 0; month < 12; month += step) {
      const day = Math.floor(Date.UTC(year, month, 1) / DAY);
      if (day < from || day > to) continue;
      ticks.push({
        day,
        label: month === 0 ? String(year) : MONTHS[month],
        major: month === 0,
      });
    }
  }
  return ticks;
}

/** "4 y 2 m", "7 m", "12 d" — how long a tenure has run */
export function lengthOf(from: string, to: string | null): string {
  const days = (to ? dayOf(to) : todayDay()) - dayOf(from);
  if (days < 45) return `${Math.max(days, 0)} d`;
  const months = Math.round(days / 30.44);
  const years = Math.floor(months / 12);
  const rest = months % 12;
  if (!years) return `${months} m`;
  return rest ? `${years} y ${rest} m` : `${years} y`;
}

/* ── Rows ─────────────────────────────────────────────────────────── */

/**
 * Where each of a seat's tenures sits: one lane while they follow one
 * another, a second when two overlap (an interim beside a holder, a seat
 * shared through a merger). Returns the lane of each tenure and how many.
 */
export function lanesOf(tenures: TenureView[]): { lane: number[]; count: number } {
  const ends: number[] = [];
  const lane = tenures.map((tenure) => {
    const start = dayOf(tenure.from);
    const end = tenure.to ? dayOf(tenure.to) : Infinity;
    const free = ends.findIndex((last) => last <= start);
    if (free === -1) {
      ends.push(end);
      return ends.length - 1;
    }
    ends[free] = end;
    return free;
  });
  return { lane, count: Math.max(ends.length, 1) };
}

/* ── People ───────────────────────────────────────────────────────── */

/** Titles, for a timeline from before the server sent `personKey` */
const RANKS = new Set([
  'general',
  'gl',
  'gal',
  'gen',
  'colonel',
  'commandant',
  'capitaine',
  'lieutenant',
  'dr',
  'dre',
  'docteur',
  'pr',
  'professeur',
  'me',
  'maitre',
  'ing',
  'ingenieur',
  'elhadj',
  'hadja',
  'monsieur',
  'madame',
  'mme',
  'de',
  'brigade',
  'division',
]);

/**
 * One person across acts that spell their title or capitals differently —
 * "Dr Bernard Goumou" and "Bernard GOUMOU" — as the server matched them
 */
export const personOf = (tenure: Pick<TenureView, 'person' | 'personKey'>): string =>
  tenure.personKey ??
  foldText(tenure.person)
    .replace(/[^a-z\s'-]/g, ' ')
    .split(/[\s'-]+/)
    .filter((word) => word && !RANKS.has(word))
    .sort()
    .join(' ');

export interface Tone {
  fill: string;
  edge: string;
  ink: string;
}

/**
 * Soft hues for people — one per person, the same in every seat they hold,
 * so a move reads at a glance. Red is kept for selection and today.
 */
const PEOPLE: Tone[] = [
  { fill: '#e0f2fe', edge: '#0284c7', ink: '#075985' },
  { fill: '#ccfbf1', edge: '#0d9488', ink: '#115e59' },
  { fill: '#ede9fe', edge: '#7c3aed', ink: '#5b21b6' },
  { fill: '#fef3c7', edge: '#d97706', ink: '#92400e' },
  { fill: '#e0e7ff', edge: '#4f46e5', ink: '#3730a3' },
  { fill: '#d1fae5', edge: '#059669', ink: '#065f46' },
  { fill: '#ffedd5', edge: '#ea580c', ink: '#9a3412' },
  { fill: '#cffafe', edge: '#0891b2', ink: '#155e75' },
  { fill: '#fae8ff', edge: '#c026d3', ink: '#86198f' },
  { fill: '#ecfccb', edge: '#65a30d', ink: '#3f6212' },
  { fill: '#e2e8f0', edge: '#475569', ink: '#1e293b' },
  { fill: '#fef9c3', edge: '#ca8a04', ink: '#854d0e' },
];

export function personTone(tenure: Pick<TenureView, 'person' | 'personKey'>): Tone {
  const key = personOf(tenure);
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return PEOPLE[hash % PEOPLE.length];
}

const TIER_TONE: Record<SeatView['tier'], Tone> = {
  1: PEOPLE[2],
  2: PEOPLE[0],
  3: PEOPLE[1],
  4: PEOPLE[10],
};

const ENDING_TONE: Record<string, Tone> = {
  current: PEOPLE[5],
  replaced: PEOPLE[10],
  moved: PEOPLE[4],
  left: PEOPLE[3],
  removed: PEOPLE[6],
};

export const ENDING_LABEL: Record<string, string> = {
  current: 'In post',
  replaced: 'Replaced',
  moved: 'Moved to another post',
  left: 'Not kept in a new government',
  removed: 'Removed',
};

export function toneOf(tenure: TenureView, tier: SeatView['tier'], by: TimelineColour): Tone {
  if (by === 'post') return TIER_TONE[tier];
  if (by === 'ending') return ENDING_TONE[tenure.to ? (tenure.ended ?? 'replaced') : 'current'];
  return personTone(tenure);
}

/** What the colours mean, for the legend */
export function legendOf(by: TimelineColour): Array<{ label: string; tone: Tone }> {
  if (by === 'post')
    return ([1, 2, 3, 4] as const).map((tier) => ({ label: TIER_LABEL[tier], tone: TIER_TONE[tier] }));
  if (by === 'ending')
    return Object.keys(ENDING_TONE).map((key) => ({ label: ENDING_LABEL[key], tone: ENDING_TONE[key] }));
  return [];
}

/* ── Acts ─────────────────────────────────────────────────────────── */

export interface ActEffects {
  /** Took a post by this act */
  began: number[];
  /** Kept in a post they already held */
  kept: number[];
  /** Whose tenure this act ended */
  ended: number[];
}

/** What each act did, read off the tenures the acts give */
export function effectsOf(view: TimelineView): Map<string, ActEffects> {
  const effects = new Map<string, ActEffects>();
  const of = (id: string) => {
    let entry = effects.get(id);
    if (!entry) {
      entry = { began: [], kept: [], ended: [] };
      effects.set(id, entry);
    }
    return entry;
  };
  view.tenures.forEach((tenure, index) => {
    of(tenure.fromAct.id).began.push(index);
    for (const act of tenure.confirmedBy) of(act.id).kept.push(index);
    if (tenure.toAct) of(tenure.toAct.id).ended.push(index);
  });
  return effects;
}

/** The seat each tenure belongs to */
/**
 * The rows of a seat: the post at the body as it is today, then — nested
 * under it — each body that ended and was replaced by it, with who held the
 * post there
 */
export function seatRows(seat: SeatView): SeatView[] {
  return [
    seat,
    ...(seat.defunct ?? []).map((body) => ({
      key: `${seat.key}|${body.label}`,
      post: seat.post,
      label: body.label,
      tier: seat.tier,
      tenures: body.tenures,
      gone: body,
    })),
  ];
}

/** "Merged into the Ministry of … · 2026" — how a body came to end */
export function goneSentence(body: DefunctBodyView): string {
  const into = body.replacedBy;
  const list = into.length <= 1 ? (into[0] ?? '') : `${into.slice(0, -1).join(', ')} and ${into[into.length - 1]}`;
  const how =
    body.how === 'split' && list
      ? `Split into ${list}`
      : body.how === 'merge' && list
        ? `Merged into ${list}`
        : body.how === 'dissolved'
          ? 'Dissolved'
          : list
            ? `Replaced by ${list}`
            : 'No longer exists';
  return body.endedOn ? `${how} · ${body.endedOn.slice(0, 4)}` : how;
}

export function seatOfTenure(view: TimelineView): Map<number, SeatView> {
  const map = new Map<number, SeatView>();
  for (const seat of view.seats ?? []) {
    for (const row of seatRows(seat)) for (const index of row.tenures) map.set(index, row);
  }
  return map;
}

/* ── Search ───────────────────────────────────────────────────────── */

export const matchesQuery = (query: string, ...texts: Array<string | null | undefined>): boolean => {
  const term = foldText(query.trim());
  if (!term) return true;
  return texts.some((text) => !!text && foldText(text).includes(term));
};

export const actMatches = (act: GovActView, query: string): boolean =>
  matchesQuery(
    query,
    act.title,
    act.reference,
    act.date,
    ...act.members.flatMap((member) => [member.name, member.title]),
  );

export const tenureMatches = (tenure: TenureView, seat: SeatView | undefined, query: string): boolean =>
  matchesQuery(query, tenure.person, tenure.title, tenure.body, seat?.label);
